var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";
// This service uses the MediaPipe Selfie Segmentation to blur the background of a webcam stream.
export class WebcamBackgroundService {
    constructor() {
        this.outputCanvas = document.createElement("canvas");
        this.processingCanvas = document.createElement("canvas");
        this.rafId = null;
        this.blurRadius = 9;
    }
    // Sets the video stream to be processed by the service.
    setVideoStream(videoStream) {
        var _a, _b;
        const video = document.createElement("video");
        video.srcObject = videoStream;
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.addEventListener("loadedmetadata", () => {
            void video.play();
        });
        this.video = video;
        this.outputCanvas.width = (_a = this.video.videoWidth) !== null && _a !== void 0 ? _a : 320;
        this.outputCanvas.height = (_b = this.video.videoHeight) !== null && _b !== void 0 ? _b : 240;
        const octx = this.outputCanvas.getContext("2d");
        if (octx == null)
            throw new Error("Konnte 2D-Context nicht anlegen.");
        this.outputCtx = octx;
        this.processingCanvas.width = this.outputCanvas.width;
        this.processingCanvas.height = this.outputCanvas.height;
        const pctx = this.processingCanvas.getContext("2d");
        if (pctx == null)
            throw new Error("Konnte Processing-Context nicht anlegen.");
        this.processingCtx = pctx;
    }
    setBlurRadius(radius) {
        if (radius < 0)
            return;
        this.blurRadius = radius;
    }
    getCanvas() {
        return this.outputCanvas;
    }
    getOutputStream(frameRate = 30) {
        return this.outputCanvas.captureStream(frameRate);
    }
    // Starts the segmentation process. If the video is not ready, it waits for the metadata to be loaded.
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.segmentation != null)
                return; // bereits aktiv
            if (this.video === undefined)
                return;
            // wait for the video to be ready
            if (this.video.readyState < 1) {
                yield new Promise((resolve) => {
                    if (this.video === undefined)
                        return;
                    this.video.addEventListener("loadedmetadata", () => {
                        resolve();
                    }, { once: true });
                });
            }
            this.outputCanvas.width = this.video.videoWidth || 320;
            this.outputCanvas.height = this.video.videoHeight || 240;
            this.processingCanvas.width = this.outputCanvas.width;
            this.processingCanvas.height = this.outputCanvas.height;
            this.segmentation = new SelfieSegmentation({
                locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
            });
            this.segmentation.setOptions({
                modelSelection: 1, // 0 = Landscape, 1 = Full-Body
                selfieMode: true
            });
            yield this.segmentation.initialize();
            const processFrame = () => __awaiter(this, void 0, void 0, function* () {
                if (this.video === undefined || this.processingCtx === undefined)
                    return;
                if (this.segmentation === undefined)
                    return;
                this.processingCtx.drawImage(this.video, 0, 0, this.processingCanvas.width, this.processingCanvas.height);
                yield this.segmentation.send({
                    image: this.processingCanvas
                });
                this.segmentation.onResults((results) => {
                    this.drawComposite(results);
                });
                this.rafId = requestAnimationFrame(processFrame);
            });
            void processFrame();
        });
    }
    // Stops the segmentation process and cleans up resources.
    stop() {
        var _a;
        if (this.rafId !== null)
            cancelAnimationFrame(this.rafId);
        void ((_a = this.segmentation) === null || _a === void 0 ? void 0 : _a.close());
        this.segmentation = undefined;
    }
    // Draws the processed video and segmentation mask onto the output canvas.
    drawComposite(results) {
        if (this.outputCtx === undefined || this.video === undefined)
            return;
        const { segmentationMask } = results;
        const { width, height } = this.outputCanvas;
        // Blur the background
        this.outputCtx.save();
        this.outputCtx.filter = `blur(${this.blurRadius}px)`;
        this.outputCtx.drawImage(this.video, 0, 0, width, height);
        this.outputCtx.restore();
        // Draw the segmentation mask
        this.outputCtx.save();
        this.outputCtx.globalCompositeOperation = "destination-out";
        this.outputCtx.scale(-1, 1);
        this.outputCtx.drawImage(segmentationMask, 0, 0, width * -1, height);
        this.outputCtx.restore();
        this.outputCtx.save();
        this.outputCtx.globalCompositeOperation = "destination-over";
        this.outputCtx.drawImage(this.video, 0, 0, width, height);
        this.outputCtx.restore();
        this.outputCtx.globalCompositeOperation = "source-over";
    }
}
export const webcamBackgroundService = new WebcamBackgroundService();
